import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";

/** Masks old state during rendering and rejects setters captured by an older scope. */
export function useScopedState<T>(key: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const identity = useRef({ key });
  if (identity.current.key !== key) identity.current = { key };
  const owner = identity.current;
  const makeInitial = () => typeof initial === "function" ? (initial as () => T)() : initial;
  const [snapshot, setSnapshot] = useState(() => ({ owner, value: makeInitial() }));
  const current = snapshot.owner === owner ? snapshot : { owner, value: makeInitial() };
  if (snapshot.owner !== owner) setSnapshot(current);
  const setValue = useCallback<Dispatch<SetStateAction<T>>>((update) => {
    if (identity.current !== owner) return;
    setSnapshot(previous => previous.owner !== owner ? previous : {
      owner,
      value: typeof update === "function" ? (update as (value: T) => T)(previous.value) : update,
    });
  }, [owner]);
  return [current.value, setValue];
}
